import React, { useMemo, useState } from "react";
import { Eye, FileText, LoaderCircle, RefreshCw, RotateCw, Trash2, X } from "lucide-react";
import {
  ACTIVE_STATUSES,
  FileNameCell,
  StatusBadge,
  formatDate,
} from "./reportRow";
import "./SensiUploadTable.css";

// An upload already on its way to the server has no cancel: the request would
// finish anyway and the report would reappear on the next refresh.
const canDelete = (row) => row.status !== "uploading" && row.status !== "processing";

// Only a stored report has anything to show: a failed one was never parsed, and
// a row still in this browser has no id to fetch by.
const canPreview = (row) => Boolean(row.id) && row.status === "success";

const FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "active", label: "In progress", match: (row) => ACTIVE_STATUSES.has(row.status) },
  { id: "success", label: "Successful", match: (row) => row.status === "success" },
  { id: "failed", label: "Failed", match: (row) => row.status === "failed" },
];

/**
 * The Delete cell for one row. Deleting a stored report is a real edit to the
 * client's record, so the trash icon asks first and the row says what will
 * happen, rather than relying on a browser confirm box.
 */
const DeleteCell = ({ row, deleting, confirming, onAsk, onCancel, onConfirm }) => {
  if (deleting) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
        <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Deleting…
      </span>
    );
  }

  if (confirming) {
    return (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={onConfirm}
          className="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
        >
          Delete
        </button>
        <button
          type="button"
          onClick={onCancel}
          aria-label={`Keep ${row.fileName}`}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onAsk}
      disabled={!canDelete(row)}
      title={
        canDelete(row) ? undefined : "Wait for this upload to finish"
      }
      aria-label={`Delete ${row.fileName}`}
      className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
    >
      <Trash2 className="h-4 w-4" aria-hidden="true" />
    </button>
  );
};

/** Every Sensi report upload with its date, page count and outcome. */
const SensiUploadTable = ({
  rows,
  loading,
  error,
  hasMore = false,
  onLoadMore,
  onRefresh,
  onRetry,
  onPreview,
  onDelete,
  deletingKeys,
  deleteError,
}) => {
  const [filter, setFilter] = useState("all");
  // Which row is showing "Delete / cancel"; only ever one at a time.
  const [confirmKey, setConfirmKey] = useState(null);

  const counts = useMemo(
    () =>
      Object.fromEntries(
        FILTERS.map(({ id, match }) => [id, rows.filter(match).length]),
      ),
    [rows],
  );
  const visible = useMemo(
    () => rows.filter(FILTERS.find((f) => f.id === filter).match),
    [rows, filter],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter uploads">
          {FILTERS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              aria-pressed={filter === id}
              onClick={() => setFilter(id)}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${
                filter === id
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {label}
              <span
                className={`tabular-nums text-xs ${
                  filter === id ? "text-slate-300" : "text-slate-400"
                }`}
              >
                {counts[id]}
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw
            className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
          Refresh
        </button>
      </div>

      {error ? (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {error}
        </p>
      ) : null}

      {deleteError ? (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {deleteError}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {visible.length === 0 ? (
          <div className="px-6 py-12 text-center">
            {loading && rows.length === 0 ? (
              <p className="text-sm text-slate-500">Loading uploads…</p>
            ) : rows.length === 0 ? (
              <>
                <FileText
                  className="mx-auto h-8 w-8 text-slate-300"
                  aria-hidden="true"
                />
                <p className="mt-3 text-sm font-medium text-slate-700">
                  No reports uploaded yet
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Uploaded Sensi reports and their status will show here.
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-500">No uploads match this filter.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="sensi-table w-full text-sm">
              {/* File name takes the slack so the three narrow columns sit
                  together at the right, each with even gutters either side. */}
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="bg-slate-50 px-4 py-3">File name</th>
                  <th scope="col" className="whitespace-nowrap bg-slate-50 px-3 py-3">Date uploaded</th>
                  <th scope="col" className="bg-slate-50 px-1 py-3 text-center">Pages</th>
                  <th scope="col" className="bg-slate-50 px-3 py-3 text-center">Status</th>
                  <th scope="col" className="bg-slate-50 px-3 py-3 text-center">Preview</th>
                  <th scope="col" className="bg-slate-50 px-3 py-3 text-center">Delete</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((row) => (
                  <tr key={row.key} className="align-top">
                    <td className="px-4 py-3">
                      <FileNameCell fileName={row.fileName} size={row.size} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                      {formatDate(row.uploadedAt)}
                    </td>
                    <td className="px-1 py-3 text-center tabular-nums text-slate-700">
                      {row.pageCount ?? "—"}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <StatusBadge status={row.status} />
                      {row.status === "uploading" ? (
                        <div className="mt-2 flex items-center justify-center gap-2">
                          <div
                            className="h-1.5 w-28 overflow-hidden rounded-full bg-slate-100"
                            role="progressbar"
                            aria-valuenow={row.progress}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Uploading ${row.fileName}`}
                          >
                            <div
                              className="h-full rounded-full bg-blue-600 transition-all"
                              style={{ width: `${row.progress}%` }}
                            />
                          </div>
                          <span className="text-xs tabular-nums text-slate-500">
                            {row.progress}%
                          </span>
                        </div>
                      ) : null}
                      {row.status === "failed" && row.error ? (
                        <p className="mx-auto mt-1 max-w-xs text-xs text-red-600">
                          {row.error}
                        </p>
                      ) : null}
                      {/* Retry sits under the status it belongs to; discarding
                          the row is the Delete column's job. */}
                      {row.status === "failed" && row.retryable ? (
                        <button
                          type="button"
                          onClick={() => onRetry(row.key)}
                          className="mt-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
                        >
                          <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                          Retry
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => onPreview(row)}
                        disabled={!canPreview(row)}
                        title={
                          canPreview(row)
                            ? undefined
                            : row.status === "failed"
                              ? "Albert couldn't read this report"
                              : "Available once the upload finishes"
                        }
                        aria-label={`Preview ${row.fileName}`}
                        className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                      >
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <DeleteCell
                        row={row}
                        deleting={deletingKeys.has(row.key)}
                        confirming={confirmKey === row.key}
                        onAsk={() => setConfirmKey(row.key)}
                        onCancel={() => setConfirmKey(null)}
                        onConfirm={() => {
                          setConfirmKey(null);
                          onDelete(row);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {hasMore ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load older reports"}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default SensiUploadTable;
